import { Controller } from "@hotwired/stimulus"

// Debounced auto-save for the Trix rich-text editor on the season rules notes.
//
// - Listens for `trix-change` events on the editor target.
// - 1.2s debounce after the last change.
// - Skips submit when content is unchanged.
// - On `beforeunload`, flushes a pending save synchronously via sendBeacon.
// - Status pill cycles: Saved → Editing… → Saving… → Saved (or Save failed).
export default class extends Controller {
  static targets = ["editor", "status"]
  static values = {
    url: String,
    debounce: { type: Number, default: 1200 },
  }

  connect() {
    this.lastSavedHTML = this.currentHTML()
    this.timer = null
    this.boundFlush = this.flushOnUnload.bind(this)
    window.addEventListener("beforeunload", this.boundFlush)
  }

  disconnect() {
    window.removeEventListener("beforeunload", this.boundFlush)
    if (this.timer) clearTimeout(this.timer)
  }

  scheduleSave() {
    this.setStatus("Editing…")
    if (this.timer) clearTimeout(this.timer)
    this.timer = setTimeout(() => this.save(), this.debounceValue)
  }

  save() {
    const html = this.currentHTML()
    if (html === this.lastSavedHTML) {
      this.setStatus("Saved")
      return
    }
    this.setStatus("Saving…")

    const formData = new FormData(this.element) // the form

    fetch(this.urlValue, {
      method: "PATCH",
      body: formData,
      headers: { Accept: "text/vnd.turbo-stream.html, text/html" },
      credentials: "same-origin",
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        this.lastSavedHTML = html
        this.setStatus("Saved")
      })
      .catch((error) => {
        this.setStatusError(`Save failed (${error.message})`)
      })
  }

  flushOnUnload() {
    if (!this.timer) return
    clearTimeout(this.timer)
    this.timer = null
    const html = this.currentHTML()
    if (html === this.lastSavedHTML) return

    const formData = new FormData(this.element)
    if (typeof navigator.sendBeacon === "function") {
      navigator.sendBeacon(this.urlValue, formData)
    }
  }

  currentHTML() {
    const trixInput = this.element.querySelector(
      "input[type='hidden'][name='season[rules_text]']",
    )
    return trixInput ? trixInput.value : ""
  }

  setStatus(text) {
    if (!this.hasStatusTarget) return
    this.statusTarget.textContent = text
    this.statusTarget.classList.remove("text-red-400")
    this.statusTarget.classList.add("text-app-text-faint")
  }

  setStatusError(text) {
    if (!this.hasStatusTarget) return
    this.statusTarget.textContent = text
    this.statusTarget.classList.remove("text-app-text-faint")
    this.statusTarget.classList.add("text-red-400")
  }
}
